import { Link } from 'react-router-dom';
import { LogOut, ChevronsLeft, ChevronsRight } from 'lucide-react';
import {
  FaLinkedin,
  FaWhatsapp,
  FaTiktok,
  FaYoutube,
  FaXTwitter,
  FaInstagram,
  FaFacebook,
} from 'react-icons/fa6';
import useHandleLogout from 'src/hooks/useHandleLogout';
import { cn } from '../ui-x';
import MenuItems from './MenuItems';
import { NAV_GROUPS } from './navItems';

// Served from `public/`, not imported: CRA runs `src` SVG imports through
// SVGR, which cannot parse this file's CorelDRAW `<style><![CDATA[...]]>`
// block. A URL keeps the artwork byte-identical to the other repos'.
const LogoLockup = `${process.env.PUBLIC_URL}/images/droomlogo.svg`;

export const socials = [
  {
    href: 'https://twitter.com/Droom__work?t=KQdoCJ4aRxXFaoRTCgu0oA&s=08/',
    label: 'X',
    icon: <FaXTwitter className='h-4 w-4' />,
  },
  {
    href: 'https://www.instagram.com/droom__work/',
    label: 'Instagram',
    icon: <FaInstagram className='h-4 w-4' />,
  },
  {
    href: 'https://www.facebook.com/droomwork',
    label: 'Facebook',
    icon: <FaFacebook className='h-4 w-4' />,
  },
  {
    href: 'https://www.linkedin.com/company/droomwork',
    label: 'LinkedIn',
    icon: <FaLinkedin className='h-4 w-4' />,
  },
  {
    href: 'https://chat.whatsapp.com/H1noCQDkzIHDnNzry0u18z?mode=ems_copy_t',
    label: 'WhatsApp',
    icon: <FaWhatsapp className='h-4 w-4' />,
  },
  {
    href: 'https://chat.whatsapp.com/H1noCQDkzIHDnNzry0u18z?mode=ems_copy_t',
    label: 'TikTok',
    icon: <FaTiktok className='h-4 w-4' />,
  },
  {
    href: 'https://chat.whatsapp.com/H1noCQDkzIHDnNzry0u18z?mode=ems_copy_t',
    label: 'YouTube',
    icon: <FaYoutube className='h-4 w-4' />,
  },
];

/** Socials sit in the rail's muted register rather than being dropped. */
export const SocialLinks = () => (
  <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
    {socials.map(({ href, label, icon }) => (
      <a
        key={label}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        aria-label={label}
        title={label}
        className='text-white/50 transition-colors hover:text-brand-orange'
      >
        {icon}
      </a>
    ))}
  </div>
);

/**
 * The client rail (reference: client.tsx) — navy, collapsible w-16/w-60. This
 * is the opposite register to the talent rail in developers-app, which is a
 * fixed white one.
 */
const SideNav = ({ collapsed, setCollapsed }) => {
  const { handleLogout } = useHandleLogout();

  return (
    <aside
      className={cn(
        'sticky top-0 hidden h-screen shrink-0 flex-col bg-brand-navy text-white transition-[width] duration-200 lg:flex',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      <div
        className={cn(
          'flex h-14 shrink-0 items-center border-b border-white/10 px-3',
          collapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {/* The original Droomwork lockup. This rail is brand-navy and the
            artwork's gradient ends on #513D24, so its lower half has very
            little separation here. width/height carry the artwork's own
            63.75:17.5 ratio so the row reserves its space before the file
            lands; `h-8 w-auto` is what sizes it. The text that used to name
            this link is gone, so the anchor carries the name itself. */}
        {!collapsed && (
          <Link
            to='/'
            aria-label='Droomwork home'
            className='flex items-center px-2'
          >
            <img
              src={LogoLockup}
              alt=''
              aria-hidden='true'
              width={255}
              height={70}
              className='h-8 w-auto'
            />
          </Link>
        )}
        <button
          type='button'
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className='rounded-md p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white'
        >
          {collapsed ? (
            <ChevronsRight className='h-4 w-4' />
          ) : (
            <ChevronsLeft className='h-4 w-4' />
          )}
        </button>
      </div>

      <nav className='flex-1 space-y-6 overflow-y-auto px-2 py-4 scrollbar-hide'>
        {NAV_GROUPS.map((group, index) => (
          <MenuItems
            key={group.label}
            label={group.label}
            items={group.items}
            badge={group.badge}
            collapsed={collapsed}
            className={index > 0 ? 'border-t border-white/10 pt-4' : undefined}
          />
        ))}
      </nav>

      <div className='shrink-0 space-y-3 border-t border-white/10 p-2 pb-4'>
        <button
          type='button'
          onClick={() => handleLogout()}
          title={collapsed ? 'Sign out' : undefined}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white',
            collapsed && 'justify-center'
          )}
        >
          <LogOut className='h-4 w-4 shrink-0' />
          {!collapsed && 'Sign out'}
        </button>

        {!collapsed && (
          <div className='px-3'>
            <div className='pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/40'>
              Follow us
            </div>
            <SocialLinks />
          </div>
        )}
      </div>
    </aside>
  );
};

export default SideNav;
